import { FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { api } from '../lib/api';

const CONTACT_EMAIL = 'info@startklar.digital';
const CONTACT_PHONE_DISPLAY = '+49 152 07 42 93 38';
const CONTACT_PHONE_TEL = '+4915207429338';

const SUBJECTS = [
  'Allgemeine Anfrage',
  'Vertrieb / Preise',
  'Technischer Support',
  'BAFA-Zulassung / Beratung',
  'Presse / Partner',
];

type FormState = {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
  consent: boolean;
  website: string;
};

interface Props {
  initialSubject?: string;
  initialMessage?: string;
  headingLevel?: 2 | 3;
}

export default function ContactSection({ initialSubject, initialMessage, headingLevel = 3 }: Props) {
  const { darkMode } = useDarkMode();

  const EMPTY: FormState = {
    name: '', email: '', phone: '', company: '',
    subject: initialSubject && SUBJECTS.includes(initialSubject) ? initialSubject : SUBJECTS[0],
    message: initialMessage ?? '',
    consent: false, website: '',
  };

  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.consent) { setError('Bitte bestätigen Sie die Datenverarbeitung, bevor Sie absenden.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/contact', form);
      setSubmitted(true);
      setForm(EMPTY);
    } catch (err: any) {
      setError(err?.message ?? 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  const headingColor = darkMode ? 'text-white' : 'text-slate-900';
  const mutedText = darkMode ? 'text-slate-400' : 'text-slate-500';
  // lg-dense: deckender als die Preiskarten. Hier steht viel Kleintext und es
  // stehen farbige Links drin — auf stark durchscheinendem Glas werden die
  // unlesbar, sobald der Lichtstrich dahinter hell wird.
  const cardBg = 'lg-surface lg-card lg-dense';
  const inputBase = `lg-field w-full px-3.5 py-2.5 text-sm ${
    darkMode ? 'text-white placeholder-slate-500' : 'text-slate-900 placeholder-slate-400'
  }`;
  const labelBase = `text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`;
  const linkClass = darkMode ? 'text-blue-400 hover:text-blue-300 underline' : 'text-blue-600 hover:text-blue-700 underline';
  const H = `h${headingLevel}` as 'h2' | 'h3';

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-8 items-start">
      {/* Sidebar */}
      <aside className={`p-6 ${cardBg}`}>
        <H className={`text-lg font-semibold mb-4 ${headingColor}`}>Direkt erreichen</H>
        <div className="space-y-4 text-sm">
          <div>
            <div className={`text-xs uppercase tracking-wide mb-1 ${mutedText}`}>E-Mail</div>
            <a href={`mailto:${CONTACT_EMAIL}`} className={linkClass}>{CONTACT_EMAIL}</a>
          </div>
          <div>
            <div className={`text-xs uppercase tracking-wide mb-1 ${mutedText}`}>Telefon</div>
            <a href={`tel:${CONTACT_PHONE_TEL}`} className={linkClass}>{CONTACT_PHONE_DISPLAY}</a>
          </div>
          <div>
            <div className={`text-xs uppercase tracking-wide mb-1 ${mutedText}`}>Anschrift</div>
            <address className={`not-italic ${mutedText}`}>
              StartKlar.Digital Agentur<br />
              Christine &amp; Andreas Helm GbR<br />
              Clara-Schumann-Str. 2<br />
              97980 Bad Mergentheim
            </address>
          </div>
          <div className={`pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <p className={`text-xs ${mutedText}`}>
              Rechtliche Hinweise finden Sie im{' '}
              <Link to="/impressum" className={linkClass}>Impressum</Link>. Informationen zur Datenverarbeitung in der{' '}
              <Link to="/datenschutz" className={linkClass}>Datenschutzerklärung</Link>.
            </p>
          </div>
        </div>
      </aside>

      {/* Formular */}
      <section className={`p-6 md:p-8 ${cardBg}`}>
        {submitted ? (
          <div className="text-center py-8">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-4 ${darkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
              <span className="text-3xl font-bold">✓</span>
            </div>
            <H className={`text-2xl font-bold mb-2 ${headingColor}`}>Nachricht gesendet</H>
            <p className={`mb-6 ${mutedText}`}>Danke für Ihre Anfrage. Wir melden uns innerhalb eines Werktages bei Ihnen.</p>
            <button type="button" onClick={() => setSubmitted(false)} className={`text-sm font-semibold ${linkClass}`}>
              Weitere Nachricht senden
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <H className={`text-xl font-semibold mb-6 ${headingColor}`}>Schreiben Sie uns eine Nachricht</H>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <label className="flex flex-col gap-1">
                <span className={labelBase}>Name <span className="text-red-500">*</span></span>
                <input type="text" required value={form.name} onChange={e => set('name', e.target.value)}
                  className={inputBase} autoComplete="name" maxLength={200} />
              </label>
              <label className="flex flex-col gap-1">
                <span className={labelBase}>E-Mail <span className="text-red-500">*</span></span>
                <input type="email" required value={form.email} onChange={e => set('email', e.target.value)}
                  className={inputBase} autoComplete="email" maxLength={200} />
              </label>
              <label className="flex flex-col gap-1">
                <span className={labelBase}>Telefon</span>
                <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                  className={inputBase} autoComplete="tel" maxLength={50} />
              </label>
              <label className="flex flex-col gap-1">
                <span className={labelBase}>Firma</span>
                <input type="text" value={form.company} onChange={e => set('company', e.target.value)}
                  className={inputBase} autoComplete="organization" maxLength={200} />
              </label>
            </div>

            <label className="flex flex-col gap-1 mb-4">
              <span className={labelBase}>Betreff <span className="text-red-500">*</span></span>
              <select required value={form.subject} onChange={e => set('subject', e.target.value)} className={inputBase}>
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>

            <label className="flex flex-col gap-1 mb-4">
              <span className={labelBase}>Nachricht <span className="text-red-500">*</span></span>
              <textarea required rows={6} value={form.message} onChange={e => set('message', e.target.value)}
                className={inputBase} minLength={10} maxLength={5000} />
            </label>

            {/* Honeypot */}
            <label aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
              Bitte leer lassen
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => set('website', e.target.value)} />
            </label>

            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={e => set('consent', e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span className={`text-xs ${mutedText}`}>
                Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden. Details zur Verarbeitung in der{' '}
                <Link to="/datenschutz" className={linkClass}>Datenschutzerklärung</Link>. Die Einwilligung kann jederzeit per E-Mail widerrufen werden.
              </span>
            </label>

            {error && (
              <div role="alert" className={`mb-4 p-3 rounded-lg text-sm border ${darkMode ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting}
              className="lg-btn w-full sm:w-auto px-6 py-3 text-sm font-semibold disabled:opacity-60">
              {submitting ? 'Wird gesendet …' : 'Nachricht senden'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
